import { Copy, Download, Edit3, Trash2, Upload } from "lucide-react";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api } from "../lib/api";
import type { DraftItem } from "../lib/types";

export function Drafts() {
  const navigate = useNavigate();
  const [items, setItems] = useState<DraftItem[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    try {
      const result = await api.drafts();
      setItems(result.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "โหลด Draft ไม่สำเร็จ");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDraft = (draft: DraftItem) => {
    localStorage.setItem("telegram-card-edit-payload", JSON.stringify(draft.payload));
    navigate("/create");
  };

  const duplicate = async (draft: DraftItem) => {
    await api.saveDraft({ name: `${draft.name} copy`, payload: draft.payload });
    setNotice("Duplicate draft แล้ว");
    load();
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "telegram-card-drafts.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const importJson = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const drafts = Array.isArray(parsed) ? parsed : [parsed];
      for (const draft of drafts) {
        if (draft?.payload) await api.saveDraft({ name: draft.name ?? "Imported draft", payload: draft.payload });
      }
      setNotice("Import draft สำเร็จ");
      load();
    } catch {
      setError("ไฟล์ JSON ไม่ถูกต้อง");
    } finally {
      event.target.value = "";
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Drafts</h1>
          <p className="mt-1 text-sm text-slate-500">บันทึก payload ของการ์ดไว้แก้ต่อหรือ export/import เป็น JSON</p>
        </div>
        <div className="flex gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            <Upload size={16} />
            Import
            <input type="file" accept="application/json" className="sr-only" onChange={importJson} />
          </label>
          <button onClick={exportJson} className="inline-flex items-center gap-2 rounded bg-ink px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {notice ? <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div> : null}
      {error ? <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((draft) => (
          <article key={draft.id} className="rounded border border-line bg-white p-4 shadow-sm">
            <h2 className="truncate text-base font-semibold text-ink">{draft.name}</h2>
            <p className="mt-1 line-clamp-3 min-h-16 whitespace-pre-wrap text-sm leading-5 text-slate-500">{draft.payload.caption || "No caption"}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => openDraft(draft)} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1.5 text-sm hover:bg-slate-100">
                <Edit3 size={15} />
                Open
              </button>
              <button onClick={() => duplicate(draft)} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1.5 text-sm hover:bg-slate-100">
                <Copy size={15} />
                Duplicate
              </button>
              <button
                onClick={async () => {
                  await api.deleteDraft(draft.id);
                  load();
                }}
                className="inline-flex items-center gap-1 rounded border border-red-200 px-2.5 py-1.5 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 size={15} />
                Delete
              </button>
            </div>
          </article>
        ))}
      </div>
      {items.length === 0 ? <div className="rounded border border-line bg-white p-6 text-sm text-slate-500">ยังไม่มี Draft</div> : null}
    </div>
  );
}
